import type { OutlinedInputProps } from '@mui/material/OutlinedInput';
import React from 'react';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import OutlinedInput from '@mui/material/OutlinedInput';
import { styled } from '@mui/material/styles';
import { useField } from 'formik';
import { isWhiteSpaceLike } from 'typescript';

import Error from '../Error';
import Label from '../Label';

const Input = styled(OutlinedInput, { label: 'Input' })<OutlinedInputProps>(({ theme }) => ({
  fontSize: '18px',
  lineHeight: '50px',
  fontWeight: 500,
  color: theme.palette.common.white,
  backgroundColor: 'rgba(255, 255, 255, 0.26)',
  borderRadius: '300px',
  padding: '10px 31px',
  minHeight: '67px',
  boxShadow: `0 0 3px 0 rgba(255, 255, 255, 0.12) inset,
    12px 12px 7px -15px #ffffff inset,
    -12px -12px 7px -15px #ffffff inset,
    0 0 0 3px rgba(153, 153, 153, 0) inset,
    0 0 rgba(242, 242, 242, 0.12) inset`,

  '& input': {
    padding: 0,
    color: `#ffffff`,

    '&:-webkit-autofill': {
      color: `${theme.palette.text.primary} !important`,
      WebkitBoxShadow: '0 0 0 1000px white inset',
      WebkitTextFillColor: 'inherit',
      transition: 'background-color 5000s ease-in-out 0s',
    },
  },

  '& fieldset': {
    border: 'none',
  },

  '&.MuiInputBase-multiline': {
    padding: '16px 12px 16px 0',
    borderRadius: '21px',

    '& textarea': {
      paddingLeft: '12px',
      lineHeight: '24px',
    },
  },
}));

const MaxChars = styled('p', { label: 'MaxChars', shouldForwardProp: prop => prop !== 'maxChars' })(({ theme }) => ({
  position: 'absolute',
  left: 0,
  top: 0,
  fontSize: '12px',
  lineHeight: '13px',
  marginTop: '5px',
  color: theme.palette.common.white,
}));

interface Props extends OutlinedInputProps {
  name: string;
  maxChars?: number;
  inputRef?: React.Ref<HTMLInputElement>;
  isRequired?: boolean;
}

const TextField = (props: Props) => {
  const { maxChars, isRequired = false, ...inputProps } = props;
  const [field, meta, helpers] = useField(props.name);
  const shouldRenderError = !!(meta.touched && meta.error);
  const wordsAmount = getWordsAmount((field.value as string) || '');

  const processTextChange = (text: string) => {
    const newAmount = getWordsAmount(text);

    if (maxChars && newAmount > maxChars) {
      const splittedWords = text.split(' ');

      void helpers.setValue(splittedWords.slice(0, maxChars).join(' '));
      return;
    }

    void helpers.setValue(text);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      // check if words limit is already exceeded:
      if (maxChars && getWordsAmount((field.value as string) || '') >= maxChars) {
        e.preventDefault();
        return;
      }
    }
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    processTextChange(e.target.value);
  };

  const onPaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    // check if words limit is already exceeded:
    if (maxChars && getWordsAmount((field.value as string) || '') > maxChars) {
      e.preventDefault();
      return;
    }
  };

  return (
    <FormControl variant="outlined" fullWidth>
      {props.label && (
        <Label isRequired={isRequired} isError={shouldRenderError}>
          {props.label}
        </Label>
      )}
      <Input
        id={props.name}
        {...field}
        {...inputProps}
        inputRef={props.inputRef}
        autoComplete="off"
        onKeyDown={onKeyDown}
        onChange={onChange}
        onPaste={onPaste}
      />
      <Box sx={{ position: 'relative' }}>
        {shouldRenderError && <Error error={meta.error as string | string[]} />}
        {maxChars && (
          <MaxChars>
            {wordsAmount}/{maxChars}
          </MaxChars>
        )}
      </Box>
    </FormControl>
  );
};

function getWordsAmount(text: string) {
  return text.split(' ').filter(s => !!s.trim()).length;
}

export default TextField;
